import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, Info } from "lucide-react";
import { SERVICES } from "../lib/services";

const FACILITY = ["Corporate / professional office", "Retail store / showroom", "Medical / dental office", "Multi-tenant common areas", "Small / midsize commercial facility", "Other"];
const SERVICE = SERVICES.map((s) => s.nav);
const SIZE = ["Under 2,500 sq ft", "2,500–5,000", "5,000–10,000", "10,000–25,000", "25,000+", "Not sure"];
const FREQ = ["1× per week", "2–3× per week", "5× per week", "Daily", "Not sure yet"];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const TIME = ["After hours", "Early morning", "During business hours", "Flexible"];
const CITY = ["Peoria", "Glendale", "Surprise", "Other West Valley"];
const CONSULT = ["On-site walkthrough", "Phone or video consultation", "Either"];
const CONTACT_PREF = ["Email", "Phone", "Either"];
const STEPS = ["Facility", "Schedule", "Requirements", "Contact"];

export const INTAKE_FIELDS = {
  facility: "Facility type", size: "Approx. square footage", sqft: "Exact square footage", city: "City", zip: "ZIP code",
  services: "Services needed", frequency: "Service frequency", days: "Preferred days", timing: "Service window", start: "Desired start date",
  notes: "Special requirements", consult: "Walkthrough preference",
  company: "Business name", name: "Contact name", role: "Job title", email: "Business email", phone: "Business phone", pref: "Preferred contact method",
};

const Multi = ({ name = "service", options, value, onChange }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((o) => {
      const on = value.includes(o);
      return (
        <button type="button" key={o} onClick={() => onChange(on ? value.filter((x) => x !== o) : [...value, o])} data-testid={`chip-${name}-${o.replace(/\W+/g, "-").toLowerCase()}`}
          className={`rounded-full border px-4 py-2.5 text-sm transition-colors duration-200 ${on ? "border-ink bg-ink text-white" : "border-slate-300 bg-white text-ink hover:border-ink"}`}>
          {o}
        </button>
      );
    })}
  </div>
);

const Chips = ({ name, options, value, onChange }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((o) => (
      <button type="button" key={o} onClick={() => onChange(o)} data-testid={`chip-${name}-${o.replace(/\W+/g, "-").toLowerCase()}`}
        className={`rounded-full border px-4 py-2.5 text-sm transition-colors duration-200 ${value === o ? "border-ink bg-ink text-white" : "border-slate-300 bg-white text-ink hover:border-ink"}`}>
        {o}
      </button>
    ))}
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-3 block font-mono text-xs uppercase tracking-[0.2em] text-slate-500">{label}</span>
    {children}
  </label>
);

const input = "w-full rounded-xl border border-slate-300 bg-white px-4 py-3.5 text-base text-ink outline-none transition-colors focus:border-ink";

const Done = ({ data, onReset }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="p-8 md:p-12" data-testid="intake-preview-complete">
    <CheckCircle2 className="h-10 w-10 text-emerald-600" />
    <h3 className="mt-6 font-display text-3xl font-semibold text-ink">Proposal request summary</h3>
    <p className="mt-4 max-w-lg text-base text-slate-600">
      This is a design preview only — <strong>nothing was sent or stored.</strong> When connected, a request like this would be reviewed, followed by a walkthrough or consultation and a customized written proposal. No price is set and nothing is booked at this stage.
    </p>
    <dl className="mt-8 grid gap-3 rounded-2xl bg-porcelain p-6 text-sm sm:grid-cols-2">
      {Object.entries(data).filter(([, v]) => v && v.length).map(([k, v]) => (
        <div key={k}><dt className="font-mono text-xs uppercase tracking-[0.15em] text-slate-500">{INTAKE_FIELDS[k]}</dt><dd className="mt-1 text-ink">{Array.isArray(v) ? v.join(", ") : v}</dd></div>
      ))}
    </dl>
    <button onClick={onReset} data-testid="intake-preview-reset" className="btn-outline-ink mt-8">Start over</button>
  </motion.div>
);

export const IntakePreview = () => {
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [d, setD] = useState(Object.fromEntries(Object.keys(INTAKE_FIELDS).map((k) => [k, k === "services" || k === "days" ? [] : ""])));
  const set = (k) => (v) => setD((p) => ({ ...p, [k]: typeof v === "string" || Array.isArray(v) ? v : v.target.value }));
  const canNext = [d.facility && d.size && d.city, d.services.length && d.frequency && d.timing, d.consult, d.company && d.name && d.email && d.phone][step];
  const last = step === STEPS.length - 1;

  if (done) return <Done data={d} onReset={() => { setDone(false); setStep(0); }} />;

  return (
    <form onSubmit={(e) => { e.preventDefault(); if (canNext) setDone(true); }} className="p-6 md:p-12" data-testid="intake-preview-form">
      <div className="mb-10 flex items-center gap-3">
        {STEPS.map((s, i) => (
          <div key={s} className="flex flex-1 flex-col gap-2">
            <div className="h-1 overflow-hidden rounded-full bg-slate-200"><motion.div className="h-full bg-ink" animate={{ width: i <= step ? "100%" : "0%" }} transition={{ duration: 0.5 }} /></div>
            <span className={`font-mono text-[11px] uppercase tracking-[0.2em] ${i <= step ? "text-ink" : "text-slate-500"}`}>{String(i + 1).padStart(2, "0")} {s}</span>
          </div>
        ))}
      </div>
      <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }} className="space-y-8">
        {step === 0 && (<>
          <Field label="Facility type"><Chips name="facility" options={FACILITY} value={d.facility} onChange={set("facility")} /></Field>
          <Field label="Approximate size"><Chips name="size" options={SIZE} value={d.size} onChange={set("size")} /></Field>
          <Field label="Exact square footage (optional)"><input inputMode="numeric" value={d.sqft} onChange={set("sqft")} data-testid="intake-sqft" className={input} placeholder="e.g. 6,200" /></Field>
          <Field label="Location"><Chips name="city" options={CITY} value={d.city} onChange={set("city")} /></Field>
          <Field label="ZIP code (optional)"><input inputMode="numeric" maxLength={5} value={d.zip} onChange={set("zip")} data-testid="intake-zip" className={`${input} max-w-[180px]`} /></Field>
        </>)}
        {step === 1 && (<>
          <Field label="Services needed (select all that apply)"><Multi options={SERVICE} value={d.services} onChange={set("services")} /></Field>
          <Field label="Desired recurring frequency"><Chips name="frequency" options={FREQ} value={d.frequency} onChange={set("frequency")} /></Field>
          <Field label="Preferred cleaning days (optional)"><Multi name="day" options={DAYS} value={d.days} onChange={set("days")} /></Field>
          <Field label="Preferred service window"><Chips name="timing" options={TIME} value={d.timing} onChange={set("timing")} /></Field>
          <Field label="Desired start date (optional)"><input type="date" value={d.start} onChange={set("start")} data-testid="intake-start-date" className={`${input} max-w-[240px]`} /></Field>
        </>)}
        {step === 2 && (<>
          <Field label="Special requirements (optional)"><textarea rows={5} value={d.notes} onChange={set("notes")} data-testid="intake-notes" className={input} placeholder="Number of restrooms, floor types, security or alarm access, areas to exclude, current pain points…" /></Field>
          <Field label="How should we confirm your scope?"><Chips name="consult" options={CONSULT} value={d.consult} onChange={set("consult")} /></Field>
        </>)}
        {step === 3 && (<div className="grid gap-6 sm:grid-cols-2">
          <Field label="Business name"><input value={d.company} onChange={set("company")} data-testid="intake-company" className={input} /></Field>
          <Field label="Contact name"><input value={d.name} onChange={set("name")} data-testid="intake-name" className={input} /></Field>
          <Field label="Job title (optional)"><input value={d.role} onChange={set("role")} data-testid="intake-role" className={input} placeholder="Office or facility manager…" /></Field>
          <Field label="Business email"><input type="email" value={d.email} onChange={set("email")} data-testid="intake-email" className={input} /></Field>
          <Field label="Business phone"><input type="tel" value={d.phone} onChange={set("phone")} data-testid="intake-phone" className={input} /></Field>
          <Field label="Preferred contact method"><Chips name="pref" options={CONTACT_PREF} value={d.pref} onChange={set("pref")} /></Field>
        </div>)}
      </motion.div>
      <div className="mt-10 flex items-center justify-between gap-4 border-t border-slate-200 pt-8">
        <button type="button" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="intake-back-btn" className="flex items-center gap-2 text-sm text-slate-600 disabled:opacity-0"><ArrowLeft className="h-4 w-4" /> Back</button>
        {!last
          ? <button type="button" disabled={!canNext} onClick={() => setStep(step + 1)} data-testid="intake-next-btn" className="btn-ink disabled:cursor-not-allowed disabled:opacity-40">Continue <ArrowRight className="h-4 w-4" /></button>
          : <button type="submit" disabled={!canNext} data-testid="quote-form-submit-btn" className="btn-ink disabled:cursor-not-allowed disabled:opacity-40">Request my proposal (preview)</button>}
      </div>
      <p className="mt-6 flex items-start gap-2 text-xs text-slate-500"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Preview only. This form does not send or store any data. Requests lead to a customized proposal — not an instant price or booking.</p>
    </form>
  );
};
